import { toast } from 'sonner'
import { paths } from '@/routes/paths'
import { useState, useEffect } from 'react'
import { fError } from '@/utils/format-error'
import { useBoolean } from '@/hooks/use-boolean'
import { useRouter } from '@/routes/hooks/use-router'
import axiosInstance, { endpoints } from '@/lib/axios'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'

import ProgramsTable from '../programs-table'
import ProgramsFilter from '../programs-filter'
import {
  type Program,
  type ProgramFilters,
  type PaginationMeta,
  type Pagination,
  ProgramType,
} from '../program.types'

const PAGE_SIZE = 10

export default function ProgramsView() {
  const router = useRouter()
  const loading = useBoolean(true)
  const [programs, setPrograms] = useState<Program[]>([])
  const [pageMeta, setPageMeta] = useState<PaginationMeta>({
    limit: PAGE_SIZE,
    page: 1,
    total: 0,
  })
  const [filters, setFilters] = useState<ProgramFilters>({
    page: 1,
    limit: PAGE_SIZE,
    search: '',
    isEnabled: '',
    publishedAt: '',
    orderBy: 'createdAt:desc',
    type: ProgramType.ADMIN,
  })

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    program: Program | null
  }>({
    open: false,
    program: null,
  })

  // Loading state for delete operation
  const [deleteLoading, setDeleteLoading] = useState(false)

  const fetchPrograms = async () => {
    try {
      loading.onTrue()

      const _filters = {
        ...filters,
        isEnabled:
          filters.isEnabled === '' ? undefined : filters.isEnabled === 'true',
        publishedAt:
          filters.publishedAt === '' ? undefined : filters.publishedAt,
      }

      const { data } = await axiosInstance.get<Pagination<Program>>(
        endpoints.programs.list,
        {
          params: _filters,
        },
      )

      setPrograms(data.items)
      setPageMeta({
        limit: data.limit,
        page: data.page,
        total: data.total,
      })
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      loading.onFalse()
    }
  }

  useEffect(() => {
    fetchPrograms()
  }, [filters])

  const handleFiltersChange = (newFilters: Partial<ProgramFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
  }

  const handleAddClick = () => {
    router.push(paths.programCreate)
  }

  const handleEdit = (program: Program) => {
    router.push(paths.programEdit(program.id))
  }

  const handleManage = (program: Program) => {
    router.push(paths.programManage(program.id))
  }

  const handleDelete = (program: Program) => {
    setConfirmDialog({
      open: true,
      program,
    })
  }

  const handleConfirmDelete = async () => {
    if (!confirmDialog.program || deleteLoading) return

    try {
      setDeleteLoading(true)

      await axiosInstance.delete(
        endpoints.programs.delete(confirmDialog.program.id),
      )
      toast.success('ลบโปรแกรมสำเร็จ')

      // Refresh the list
      fetchPrograms()
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      setDeleteLoading(false)
      setConfirmDialog({ open: false, program: null })
    }
  }

  const handlePageChange = (page: number) => {
    setFilters({
      ...filters,
      page,
    })
  }

  return (
    <div className="w-full">
      <LoadingSection loading={loading.value}>
        <div className="space-y-6">
          {/* Filter Section */}
          <ProgramsFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onAddClick={handleAddClick}
          />

          {/* Table Section */}
          <ProgramsTable
            programs={programs}
            loading={loading.value}
            onView={() => {}}
            onEdit={handleEdit}
            onManage={handleManage}
            onDelete={handleDelete}
          />

          {/* Pagination */}
          {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
            <CustomPagination
              currentPage={filters.page}
              totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </LoadingSection>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open, program: null })}
        title="ยืนยันการลบ"
        description={`คุณต้องการลบโปรแกรม "${confirmDialog.program?.title}" หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  )
}
